import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  FaCheck,
  FaCompress,
  FaExpand,
  FaMinus,
  FaPlus,
  FaShoppingCart,
  FaTrash,
  FaUpload,
} from 'react-icons/fa';
import { api } from '../../api';
import DatePicker from '../../../components/ui/DatePicker';
import {
  buildArtworkBriefLines,
  createArtworkState,
  createArtworkTransforms,
  clampTransformsForProduct,
  getArtworkConfig,
  EXPANDED_SCALE_MAX,
  getArtworkSides,
  getSidePlacementSpec,
} from './merchPlaygroundConfig';
import StudioCanvas2D from './StudioCanvas2D';
import StudioMockupComposer from './StudioMockupComposer';
import StudioPreviewPanel from './StudioPreviewPanel';
import { getPrintRect } from './studio2dConfig';

const OPEN_MERCH_PLAYGROUND_EVENT = 'thatshirtshow:open-merch-playground';
const CLOSE_MERCH_PLAYGROUND_EVENT = 'thatshirtshow:close-merch-playground';
const STUDIO_STORAGE_KEY = 'thatshirtshow:drop-studio-session';
const STUDIO_ARTWORK_STORAGE_KEY = 'thatshirtshow:drop-studio-artwork';

const MERCH_COLOR_OPTIONS = ['White', 'Pink', 'Baby Blue', 'Stone', 'Grey', 'Black'];

const PRODUCT_OPTIONS = {
  tshirt: {
    label: 'T-shirt',
    attributeLabel: 'Colour',
    colors: MERCH_COLOR_OPTIONS,
    optionLabel: 'Size',
    options: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
  },
  hoody: {
    label: 'Hoodie',
    attributeLabel: 'Colour',
    colors: MERCH_COLOR_OPTIONS,
    optionLabel: 'Size',
    options: ['S', 'M', 'L', 'XL', '2XL'],
  },
};

const COLOR_HEX = {
  White: '#f3f2ef',
  Black: '#151515',
  Pink: '#f4a7c5',
  Stone: '#d8cfb9',
  Grey: '#8f9291',
  'Baby Blue': '#9bd5ef',
};

const BUILD_ITEM_TABS = [
  { key: 'layers', label: 'Layers' },
  { key: 'colors', label: 'Colors and quantities' },
  { key: 'instructions', label: 'Special instructions' },
];

const getDefaultSelectedColors = (product) => (
  product.singleQuantity ? [] : ['White']
);

const getColorBreakdownTotal = (breakdown = {}) => (
  Object.values(breakdown).reduce((colorTotal, sizeMap) => (
    colorTotal + Object.values(sizeMap || {}).reduce((total, quantity) => total + (Number(quantity) || 0), 0)
  ), 0)
);

const getColorQuantityTotal = (breakdown = {}, colorName) => (
  Object.values(breakdown[colorName] || {}).reduce((total, quantity) => total + (Number(quantity) || 0), 0)
);

const getColorBreakdownEntries = (breakdown = {}) => Object.entries(breakdown)
  .map(([colorName, sizeMap]) => ({
    color: colorName,
    sizes: Object.entries(sizeMap || {})
      .filter(([, quantity]) => Number(quantity) > 0)
      .map(([option, quantity]) => ({ option, quantity: Number(quantity) })),
  }))
  .filter((entry) => entry.sizes.length);

const sizeCode = (value) => {
  const normalized = value.toLowerCase();
  if (normalized === 'xs') return 'xs';
  if (normalized === 's' || normalized === 'small') return 's';
  if (normalized === 'm' || normalized === 'medium') return 'm';
  if (normalized === 'l' || normalized === 'large') return 'l';
  if (normalized === 'xl' || normalized === '2xl') return normalized;
  return normalized.replace(/\s+/g, '-');
};

const getColorPreference = (color) => {
  return color.toLowerCase().replace(/\s+/g, '-');
};

const createStudioSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `drop-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const revokeArtworkUrl = (url) => {
  if (url?.startsWith?.('blob:')) URL.revokeObjectURL(url);
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const dataUrlToFile = async (dataUrl, name, type = 'image/png') => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], name || 'drop-studio-artwork.png', { type: blob.type || type });
};

const safeUploadName = (value, fallback = 'artwork') => (
  (value || fallback)
    .toString()
    .trim()
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || fallback
);

const dataUrlToWebPFile = async (dataUrl, name, quality = 0.86) => {
  const image = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width || 1;
  canvas.height = image.naturalHeight || image.height || 1;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/webp', quality);
  });

  if (!blob) return dataUrlToFile(dataUrl, name, 'image/png');

  return new File([blob], `${safeUploadName(name)}.webp`, { type: 'image/webp' });
};

const STENCIL_EXPORT_SIZE = 1200;
const DEFAULT_ARTWORK_LAYER_SCALE = 1;

const createArtworkLayersState = (productType) => Object.fromEntries(
  getArtworkSides(productType).map((side) => [side, []])
);

const loadImageElement = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = url;
});

const flattenArtworkLayers = async (layers = [], productType = 'tshirt', side = 'front') => {
  if (!layers.length) return null;
  const printRect = getPrintRect(productType, side, 'standard');
  const canvas = document.createElement('canvas');
  canvas.width = STENCIL_EXPORT_SIZE;
  canvas.height = Math.max(1, Math.round(STENCIL_EXPORT_SIZE * (printRect.height / printRect.width)));
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);

  const renderLayers = [...layers].reverse();
  const images = await Promise.all(renderLayers.map((layer) => loadImageElement(layer.url)));
  renderLayers.forEach((layer, index) => {
    const image = images[index];
    const scaleX = Math.max(0.05, layer.scaleX ?? layer.scale ?? DEFAULT_ARTWORK_LAYER_SCALE);
    const aspectRatio = Number.isFinite(layer.aspectRatio) && layer.aspectRatio > 0
      ? layer.aspectRatio
      : image.naturalWidth / Math.max(1, image.naturalHeight);
    const drawWidth = canvas.width * scaleX;
    const drawHeight = drawWidth / Math.max(0.001, aspectRatio);
    const centerX = canvas.width / 2 + (layer.x || 0) * (canvas.width / 2);
    const centerY = canvas.height / 2 + (layer.y || 0) * (canvas.height / 2);
    context.drawImage(image, centerX - drawWidth / 2, centerY - drawHeight / 2, drawWidth, drawHeight);
  });

  return canvas.toDataURL('image/png');
};

const serializeArtworkState = (artworkState = {}) => Object.fromEntries(
  Object.entries(artworkState).map(([side, item]) => [
    side,
    item
      ? {
        name: item.name,
        type: item.type,
        dataUrl: item.dataUrl || item.url,
        url: item.dataUrl || item.url,
      }
      : null,
  ])
);

const serializeArtworkStateForStorage = (artworkState = {}) => Object.fromEntries(
  Object.keys(artworkState).map((side) => [side, null])
);

const serializeArtworkLayersForStorage = (layersBySide = {}) => Object.fromEntries(
  Object.entries(layersBySide).map(([side, layers]) => [
    side,
    (layers || []).map((layer) => ({
      id: layer.id,
      name: layer.name,
      type: layer.type,
      x: layer.x,
      y: layer.y,
      scale: layer.scale,
      scaleX: layer.scaleX,
      scaleY: layer.scaleY,
      aspectRatio: layer.aspectRatio,
    })),
  ])
);

const serializeArtworkLayersWithData = (layersBySide = {}) => Object.fromEntries(
  Object.entries(layersBySide).map(([side, layers]) => [
    side,
    (layers || []).map((layer) => ({
      id: layer.id,
      name: layer.name,
      type: layer.type,
      dataUrl: layer.dataUrl || layer.url,
      url: layer.dataUrl || layer.url,
      x: layer.x,
      y: layer.y,
      scale: layer.scale,
      scaleX: layer.scaleX,
      scaleY: layer.scaleY,
      aspectRatio: layer.aspectRatio,
    })),
  ])
);

const persistArtworkSession = (sessionId, artworkLayers, studioItems = []) => {
  if (typeof window === 'undefined' || !sessionId) return;
  try {
    window.sessionStorage.setItem(STUDIO_ARTWORK_STORAGE_KEY, JSON.stringify({
      sessionId,
      artworkLayers: serializeArtworkLayersWithData(artworkLayers),
      studioItems: serializeStudioItems(studioItems),
      updatedAt: new Date().toISOString(),
    }));
  } catch (error) {
    console.warn('Could not persist studio artwork session', error);
  }
};

const clearArtworkSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(STUDIO_ARTWORK_STORAGE_KEY);
};

const clearStoredStudioSession = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STUDIO_STORAGE_KEY);
  window.sessionStorage.removeItem(STUDIO_ARTWORK_STORAGE_KEY);
};

const restoreArtworkSession = (sessionId) => {
  if (typeof window === 'undefined' || !sessionId) return null;
  try {
    const stored = window.sessionStorage.getItem(STUDIO_ARTWORK_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.sessionId !== sessionId) return null;
    return {
      artworkLayers: parsed.artworkLayers || null,
      studioItems: parsed.studioItems || null,
    };
  } catch (error) {
    console.warn('Could not restore studio artwork session', error);
    return null;
  }
};

const serializeArtworkEntryMetadata = (entry) => ({
  side: entry.side,
  label: entry.label,
  name: entry.name,
  type: entry.type,
});

const serializeStudioItems = (items = []) => items.map((item) => ({
  ...item,
  artwork: item.artwork.map((entry) => ({
    ...serializeArtworkEntryMetadata(entry),
    dataUrl: entry.dataUrl,
    layers: entry.layers || [],
  })),
}));

const serializeStudioItemsForSubmission = (items = []) => items.map((item) => ({
  ...item,
  artwork: item.artwork.map((entry) => ({
    ...serializeArtworkEntryMetadata(entry),
    layers: (entry.layers || []).map((layer) => ({
      id: layer.id,
      name: layer.name,
      type: layer.type,
      x: layer.x,
      y: layer.y,
      scale: layer.scale,
      scaleX: layer.scaleX,
      scaleY: layer.scaleY,
      aspectRatio: layer.aspectRatio,
    })),
  })),
}));

const serializeStudioItemsForStorage = (items = []) => items.map((item) => ({
  ...item,
  artwork: item.artwork.map((entry) => ({
    ...serializeArtworkEntryMetadata(entry),
    layers: (entry.layers || []).map((layer) => ({
      id: layer.id,
      name: layer.name,
      type: layer.type,
      x: layer.x,
      y: layer.y,
      scale: layer.scale,
    })),
  })),
}));

const summarizeStudioItem = (item) => {
  const quantityLines = item.quantities.length
    ? item.quantities.map(({ option, quantity }) => `  - ${quantity} x ${option}`).join('\n')
    : '  - No quantities selected';
  const colorLines = item.colorBreakdown?.length
    ? item.colorBreakdown.map((entry) => (
      `  - ${entry.color}: ${entry.sizes.map(({ quantity, option }) => `${quantity} x ${option}`).join(', ')}`
    )).join('\n')
    : `  - ${item.selectedColors?.join(', ') || item.attributeValue || 'No colours selected'}`;
  const transformLines = item.artworkTransforms
    ? Object.entries(item.artworkTransforms).map(([side, transform]) => (
      `  - ${side}: ${transform.mode === 'expanded' ? 'expanded surface' : 'standard print'}, x ${Number(transform.offsetX || 0).toFixed(2)}, y ${Number(transform.offsetY || 0).toFixed(2)}, size ${Number(transform.scale || 1).toFixed(2)}`
    )).join('\n')
    : '  - No placement captured';
  const artworkLines = item.artworkLines || '  - No artwork uploaded';

  return [
    `- ${item.productLabel}`,
    `  ${item.attributeLabel}: ${item.attributeValue}`,
    '  Quantities:',
    quantityLines,
    '  Colours:',
    colorLines,
    item.itemInstructions ? `  Special instructions: ${item.itemInstructions}` : '',
    '  Artwork:',
    artworkLines,
    '  Artwork placement:',
    transformLines,
  ].filter(Boolean).join('\n');
};

const buildBrief = ({ studioItems, currentItem, notes }) => {
  const items = studioItems.length ? studioItems : currentItem ? [currentItem] : [];
  const itemLines = items.length
    ? items.map(summarizeStudioItem).join('\n\n')
    : '- No items added yet';

  return [
    'Design Studio request',
    '',
    'Order summary:',
    itemLines,
    '',
    'Notes:',
    notes || 'No extra notes supplied.',
  ].join('\n');
};

const MerchPlayground = ({ globalOnly = false }) => {
  const skipProductResetRef = useRef(false);
  const hasRestoredSessionRef = useRef(false);
  const [productType, setProductType] = useState('tshirt');
  const [formStep, setFormStep] = useState('build');
  const [view, setView] = useState('front');
  const [selectedArtworkSide, setSelectedArtworkSide] = useState('front');
  const [artworkTransforms, setArtworkTransforms] = useState(() => createArtworkTransforms('tshirt'));
  const [artworkTransformLimits, setArtworkTransformLimits] = useState({});
  const [color, setColor] = useState(PRODUCT_OPTIONS.tshirt.colors[0]);
  const [selectedColors, setSelectedColors] = useState(() => getDefaultSelectedColors(PRODUCT_OPTIONS.tshirt));
  const [colorQuantities, setColorQuantities] = useState({});
  const [itemInstructions, setItemInstructions] = useState('');
  const [buildItemTab, setBuildItemTab] = useState('layers');
  const [selectedOption, setSelectedOption] = useState(PRODUCT_OPTIONS.tshirt.options[0]);
  const [artwork, setArtwork] = useState(() => createArtworkState('tshirt'));
  const [artworkLayers, setArtworkLayers] = useState(() => createArtworkLayersState('tshirt'));
  const [selectedArtworkLayerId, setSelectedArtworkLayerId] = useState('');
  const artworkRef = useRef(artwork);
  const artworkInputRefs = useRef({});
  const [quantities, setQuantities] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', phone: '', expectedDropDate: '', notes: '' });
  const [isFullScreen, setIsFullScreen] = useState(globalOnly);
  const [isGlobalOpen, setIsGlobalOpen] = useState(false);
  const [studioItems, setStudioItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [studioSessionId, setStudioSessionId] = useState('');
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '', progress: 0, detail: '' });
  const [submitResetCountdown, setSubmitResetCountdown] = useState(null);
  const [workspaceMode, setWorkspaceMode] = useState('edit');
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasResetSignal, setCanvasResetSignal] = useState(0);

  const product = PRODUCT_OPTIONS[productType];
  const isGarmentListStep = formStep !== 'build';
  const artworkConfig = getArtworkConfig(productType);
  const artworkSides = artworkConfig.sides;
  const productColor = COLOR_HEX[color] || COLOR_HEX[selectedColors[0]] || '#f3f2ef';
  const selectedQuantityEntries = Object.entries(quantities).filter(([, quantity]) => quantity > 0);
  const colorBreakdownEntries = getColorBreakdownEntries(colorQuantities);
  const colorBreakdownTotal = getColorBreakdownTotal(colorQuantities);
  const checkedPreviewColors = useMemo(() => {
    if (product.singleQuantity) return product.colors;
    return product.colors.filter((colorName) => selectedColors.includes(colorName));
  }, [product.colors, product.singleQuantity, selectedColors]);
  const previewColorTotals = useMemo(() => (
    Object.fromEntries(checkedPreviewColors.map((colorName) => [
      colorName,
      getColorQuantityTotal(colorQuantities, colorName),
    ]))
  ), [checkedPreviewColors, colorQuantities]);
  const activeOption = product.singleQuantity ? selectedOption : selectedQuantityEntries[0]?.[0] || product.options[0];
  const currentQuantityTotal = selectedQuantityEntries.reduce((total, [, quantity]) => total + quantity, 0) + colorBreakdownTotal;
  const selectedArtworkLayers = artworkLayers[selectedArtworkSide] || [];
  const selectedArtworkLayer = selectedArtworkLayers.find((layer) => layer.id === selectedArtworkLayerId) || selectedArtworkLayers[0];
  const hasArtwork = artworkSides.some((side) => (artworkLayers[side] || []).length || artwork[side]);
  const canAddCurrentItem = currentQuantityTotal > 0 && hasArtwork;
  const currentItem = useMemo(() => {
    if (!currentQuantityTotal && !hasArtwork) return null;

    return {
      id: editingItemId || `${productType}-${product.singleQuantity ? selectedOption : color}`,
      productType,
      productLabel: product.label,
      attributeLabel: product.singleQuantity ? 'Shape' : 'Colour',
      attributeValue: product.singleQuantity ? selectedOption : selectedColors.join(', '),
      selectedColors,
      quantities: selectedQuantityEntries.map(([option, quantity]) => ({ option, quantity })),
      colorBreakdown: colorBreakdownEntries,
      itemInstructions: itemInstructions.trim(),
      artwork: artworkSides
        .filter((side) => artwork[side]?.file || artwork[side]?.dataUrl || artwork[side]?.url)
        .map((side) => ({
          side,
          label: artworkConfig.labels[side] || side,
          name: artwork[side].name,
          file: artwork[side].file,
          dataUrl: artwork[side].dataUrl,
          type: artwork[side].type,
          layers: artworkLayers[side] || [],
        })),
      artworkLines: buildArtworkBriefLines(artwork, productType)
        .split('\n')
        .map((line) => `  - ${line}`)
        .join('\n'),
      artworkTransforms,
      selectedArtworkSide,
      view,
    };
  }, [
    artwork,
    artworkConfig.labels,
    artworkLayers,
    artworkTransforms,
    artworkSides,
    color,
    colorBreakdownEntries,
    currentQuantityTotal,
    editingItemId,
    hasArtwork,
    itemInstructions,
    product.label,
    product.singleQuantity,
    productType,
    selectedColors,
    selectedArtworkSide,
    selectedOption,
    selectedQuantityEntries,
    view,
  ]);
  const isStudioVisible = globalOnly ? isGlobalOpen : true;
  const isStudioFullscreen = globalOnly ? isGlobalOpen : isFullScreen;
  const brief = useMemo(
    () => buildBrief({
      studioItems,
      currentItem,
      notes: contact.notes,
    }),
    [studioItems, currentItem, contact.notes]
  );

  const resetArtworkForProduct = (nextProductType) => {
    setArtwork((currentArtwork) => {
      Object.values(currentArtwork).forEach((item) => {
        revokeArtworkUrl(item?.url);
      });
      return createArtworkState(nextProductType);
    });
    setArtworkLayers(createArtworkLayersState(nextProductType));
    setSelectedArtworkLayerId('');
  };

  const resetSubmittedStudioSession = () => {
    const nextSessionId = createStudioSessionId();
    clearStoredStudioSession();
    setStudioItems([]);
    setEditingItemId(null);
    setStudioSessionId(nextSessionId);
    setContact({ name: '', email: '', phone: '', expectedDropDate: '', notes: '' });
    setFormStep('build');
    setWorkspaceMode('edit');
    setQuantities({});
    setColorQuantities({});
    setItemInstructions('');
    setBuildItemTab('layers');
    setArtworkTransforms(createArtworkTransforms(productType));
    resetArtworkForProduct(productType);
    setSubmitResetCountdown(null);
    setSubmitState({ status: 'idle', message: '', progress: 0, detail: '' });
  };

  useEffect(() => {
    setArtworkTransformLimits({});

    if (skipProductResetRef.current) {
      skipProductResetRef.current = false;
      return;
    }

    const nextProduct = PRODUCT_OPTIONS[productType];
    setColor(nextProduct.colors[0]);
    setSelectedColors(getDefaultSelectedColors(nextProduct));
    setColorQuantities({});
    setItemInstructions('');
    setBuildItemTab('layers');
    setSelectedOption(nextProduct.options[0]);
    setQuantities({});
    setView('front');
    setSelectedArtworkSide(getArtworkSides(productType)[0]);
    setArtworkTransforms((currentTransforms) => clampTransformsForProduct(productType, currentTransforms));
  }, [productType]);

  useEffect(() => {
    if (!artworkSides.includes(view)) {
      setView(artworkSides[0] || 'front');
    }
  }, [artworkSides, view]);

  useEffect(() => {
    if (product.singleQuantity) return;

    if (!checkedPreviewColors.length) {
      const fallbackColor = product.colors[0];
      setSelectedColors([fallbackColor]);
      setColor(fallbackColor);
      return;
    }

    if (!checkedPreviewColors.includes(color)) {
      setColor(checkedPreviewColors[0]);
    }
  }, [checkedPreviewColors, color, product.colors, product.singleQuantity]);

  useEffect(() => {
    if (typeof window === 'undefined' || hasRestoredSessionRef.current) return;
    hasRestoredSessionRef.current = true;

    const stored = window.localStorage.getItem(STUDIO_STORAGE_KEY);
    if (!stored) {
      const nextSessionId = createStudioSessionId();
      setStudioSessionId(nextSessionId);
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setStudioSessionId(parsed.sessionId || createStudioSessionId());
      if (parsed.productType && PRODUCT_OPTIONS[parsed.productType]) {
        skipProductResetRef.current = parsed.productType !== productType;
        setProductType(parsed.productType);
      }
      if (parsed.formStep) setFormStep(parsed.formStep);
      if (parsed.view) setView(parsed.view);
      if (parsed.selectedArtworkSide) setSelectedArtworkSide(parsed.selectedArtworkSide);
      if (parsed.artworkTransforms) setArtworkTransforms(parsed.artworkTransforms);
      if (parsed.color) setColor(parsed.color);
      if (Array.isArray(parsed.selectedColors)) setSelectedColors(parsed.selectedColors);
      if (parsed.colorQuantities) setColorQuantities(parsed.colorQuantities);
      if (parsed.itemInstructions) setItemInstructions(parsed.itemInstructions);
      if (parsed.workspaceMode) setWorkspaceMode(parsed.workspaceMode);
      setBuildItemTab('layers');
      if (parsed.selectedOption) setSelectedOption(parsed.selectedOption);
      if (parsed.quantities) setQuantities(parsed.quantities);
      if (parsed.contact) setContact(parsed.contact);
      if (Array.isArray(parsed.studioItems)) setStudioItems(parsed.studioItems);
      if (parsed.editingItemId) setEditingItemId(parsed.editingItemId);
      const restoredSessionId = parsed.sessionId || createStudioSessionId();
      const restoredArtworkLayers = restoreArtworkSession(restoredSessionId);
      if (restoredArtworkLayers?.studioItems) {
        setStudioItems(restoredArtworkLayers.studioItems);
      }
      if (restoredArtworkLayers?.artworkLayers) {
        setArtworkLayers(restoredArtworkLayers.artworkLayers);
      } else if (parsed.artworkLayers) {
        setArtworkLayers(parsed.artworkLayers);
      }
      if (parsed.artwork) setArtwork(parsed.artwork);
      if (parsed.selectedArtworkLayerId) setSelectedArtworkLayerId(parsed.selectedArtworkLayerId);
    } catch (error) {
      console.error('Could not restore drop studio session', error);
      const nextSessionId = createStudioSessionId();
      setStudioSessionId(nextSessionId);
    }
  }, [productType]);

  useEffect(() => {
    if (typeof window === 'undefined' || !hasRestoredSessionRef.current) return;
    const sessionId = studioSessionId || createStudioSessionId();
    if (!studioSessionId) setStudioSessionId(sessionId);

    const snapshot = {
      sessionId,
      productType,
      formStep,
      view,
      selectedArtworkSide,
      selectedArtworkLayerId,
      artworkTransforms,
      artworkLayers: serializeArtworkLayersForStorage(artworkLayers),
      color,
      selectedColors,
      colorQuantities,
      itemInstructions,
      workspaceMode,
      buildItemTab,
      selectedOption,
      artwork: serializeArtworkStateForStorage(artwork),
      quantities,
      contact,
      studioItems: serializeStudioItemsForStorage(studioItems),
      editingItemId,
      updatedAt: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STUDIO_STORAGE_KEY, JSON.stringify(snapshot));
      persistArtworkSession(sessionId, artworkLayers, studioItems);
    } catch (error) {
      console.warn('Could not persist drop studio session', error);
    }
  }, [
    artwork,
    artworkLayers,
    artworkTransforms,
    color,
    selectedColors,
    colorQuantities,
    itemInstructions,
    workspaceMode,
    buildItemTab,
    contact,
    formStep,
    editingItemId,
    productType,
    quantities,
    selectedArtworkSide,
    selectedArtworkLayerId,
    selectedOption,
    studioItems,
    studioSessionId,
    view,
  ]);

  useEffect(() => {
    artworkRef.current = artwork;
  }, [artwork]);

  useEffect(() => {
    if (submitState.status !== 'success' || submitResetCountdown === null) return undefined;

    if (submitResetCountdown <= 0) {
      resetSubmittedStudioSession();
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSubmitResetCountdown((currentCountdown) => (
        currentCountdown === null ? null : Math.max(0, currentCountdown - 1)
      ));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [submitState.status, submitResetCountdown]);

  useEffect(() => {
    let cancelled = false;

    const flattenSides = async () => {
      const flattenedEntries = await Promise.all(artworkSides.map(async (side) => {
        const layers = artworkLayers[side] || [];
        const dataUrl = await flattenArtworkLayers(layers, productType, side);
        return [side, dataUrl];
      }));

      if (cancelled) return;

      setArtwork((currentArtwork) => {
        const nextArtwork = { ...currentArtwork };
        flattenedEntries.forEach(([side, dataUrl]) => {
          const layers = artworkLayers[side] || [];
          nextArtwork[side] = dataUrl ? {
            file: null,
            name: `${side}-stencil.png`,
            type: 'image/png',
            dataUrl,
            url: dataUrl,
            layers,
          } : null;
        });
        return nextArtwork;
      });
    };

    flattenSides().catch((error) => {
      console.error('Could not flatten artwork stencil', error);
    });

    return () => {
      cancelled = true;
    };
  }, [artworkLayers, artworkSides, productType]);

  useEffect(() => () => {
    Object.values(artworkRef.current).forEach((item) => {
      revokeArtworkUrl(item?.url);
    });
  }, []);

  useEffect(() => {
    if (!isStudioFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousOverscrollBehavior = document.body.style.overscrollBehavior;
    const previousRootOverflow = document.documentElement.style.overflow;
    const previousRootOverscrollBehavior = document.documentElement.style.overscrollBehavior;
    document.body.classList.add('merch-playground-open');
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';

    return () => {
      document.body.classList.remove('merch-playground-open');
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscrollBehavior;
      document.documentElement.style.overflow = previousRootOverflow;
      document.documentElement.style.overscrollBehavior = previousRootOverscrollBehavior;
    };
  }, [isStudioFullscreen]);

  useEffect(() => {
    if (!globalOnly) return undefined;

    const openStudio = () => {
      setIsGlobalOpen(true);
      setIsFullScreen(true);
    };
    const openStudioFromHash = () => {
      if (window.location.hash === '#merch-playground') openStudio();
    };

    openStudioFromHash();
    window.addEventListener(OPEN_MERCH_PLAYGROUND_EVENT, openStudio);
    window.addEventListener('hashchange', openStudioFromHash);

    return () => {
      window.removeEventListener(OPEN_MERCH_PLAYGROUND_EVENT, openStudio);
      window.removeEventListener('hashchange', openStudioFromHash);
    };
  }, [globalOnly]);

  const resetCurrentBuild = () => {
    setEditingItemId(null);
    setArtworkTransformLimits({});
    setQuantities({});
    setSelectedColors(getDefaultSelectedColors(product));
    setColor(product.colors[0]);
    setColorQuantities({});
    setItemInstructions('');
    setBuildItemTab('layers');
    setView('front');
    setSelectedArtworkSide(getArtworkSides(productType)[0]);
    setArtworkTransforms(createArtworkTransforms(productType));
    setWorkspaceMode('edit');
    resetArtworkForProduct(productType);
    clearArtworkSession();
    setSubmitState({ status: 'idle', message: '' });
  };

  const addCurrentItemToStudio = () => {
    if (!currentItem || !canAddCurrentItem) {
      setSubmitState({ status: 'error', message: 'Add at least one quantity and upload artwork before adding this product.' });
      return;
    }

    setStudioItems((items) => {
      const nextItem = {
        ...currentItem,
        id: editingItemId || `${currentItem.productType}-${Date.now()}-${items.length}`,
      };

      if (editingItemId) {
        return items.map((item) => (item.id === editingItemId ? nextItem : item));
      }

      return [...items, nextItem];
    });
    setSubmitState({ status: 'idle', message: '' });
    resetCurrentBuild();
    setFormStep('list');
  };

  const removeStudioItem = (id) => {
    const nextItems = studioItems.filter((item) => item.id !== id);
    setStudioItems(nextItems);
    if (!nextItems.length && formStep === 'list') setFormStep('build');
  };

  const editStudioItem = (item) => {
    const nextProduct = PRODUCT_OPTIONS[item.productType] || PRODUCT_OPTIONS.tshirt;
    skipProductResetRef.current = item.productType !== productType;
    setArtworkTransformLimits({});
    setEditingItemId(item.id);
    setProductType(item.productType);
    setColor(nextProduct.colors[0]);
    setSelectedColors(nextProduct.singleQuantity ? [] : item.selectedColors?.length ? item.selectedColors : getDefaultSelectedColors(nextProduct));
    setColorQuantities(Object.fromEntries((item.colorBreakdown || []).map((entry) => [
      entry.color,
      Object.fromEntries((entry.sizes || []).map(({ option, quantity }) => [option, quantity])),
    ])));
    setItemInstructions(item.itemInstructions || '');
    setBuildItemTab('layers');
    setSelectedOption(nextProduct.singleQuantity ? item.attributeValue : nextProduct.options[0]);
    setQuantities(Object.fromEntries(item.quantities.map(({ option, quantity }) => [option, quantity])));
    setArtworkTransforms(clampTransformsForProduct(item.productType, item.artworkTransforms || {}));
    setSelectedArtworkSide(item.selectedArtworkSide || getArtworkSides(item.productType)[0]);
    setView(item.view || 'front');
    setArtwork(() => {
      const nextArtwork = createArtworkState(item.productType);
      item.artwork.forEach((entry) => {
        nextArtwork[entry.side] = {
          file: entry.file || null,
          name: entry.name,
          type: entry.type,
          dataUrl: entry.dataUrl || entry.url,
          url: entry.dataUrl || entry.url,
        };
      });
      return nextArtwork;
    });
    setArtworkLayers(() => {
      const nextLayers = createArtworkLayersState(item.productType);
      item.artwork.forEach((entry) => {
        nextLayers[entry.side] = entry.layers || [];
      });
      return nextLayers;
    });
    setSelectedArtworkLayerId(item.artwork.find((entry) => entry.layers?.length)?.layers?.[0]?.id || '');
    setSubmitState({ status: 'idle', message: '' });
    setFormStep('build');
  };

  const handleArtworkUpload = async (side, files) => {
    const uploadFiles = Array.from(files || []);
    if (!uploadFiles.length) return;

    setSelectedArtworkSide(side);
    setWorkspaceMode('edit');
    if (artworkSides.includes(side)) setView(side);

    const nextLayers = await Promise.all(uploadFiles.map(async (file, index) => {
      const dataUrl = await fileToDataUrl(file);
      const image = await loadImageElement(dataUrl);
      return {
        id: `${side}-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        type: file.type,
        dataUrl,
        url: dataUrl,
        x: 0,
        y: 0,
        scale: DEFAULT_ARTWORK_LAYER_SCALE,
        scaleX: DEFAULT_ARTWORK_LAYER_SCALE,
        scaleY: DEFAULT_ARTWORK_LAYER_SCALE,
        aspectRatio: image.width / Math.max(1, image.height),
      };
    }));

    setSelectedArtworkLayerId(nextLayers[0]?.id || '');
    setArtworkLayers((currentLayers) => ({
      ...currentLayers,
      [side]: [
        ...nextLayers,
        ...(currentLayers[side] || []),
      ],
    }));
  };

  const handleArtworkLayerSelect = (side, layerId) => {
    setSelectedArtworkSide(side);
    setSelectedArtworkLayerId(layerId);
    if (artworkSides.includes(side)) setView(side);
  };

  const handleArtworkLayerTransformChange = (side, layerId, patch) => {
    setArtworkLayers((currentLayers) => ({
      ...currentLayers,
      [side]: (currentLayers[side] || []).map((layer) => {
        if (layer.id !== layerId) return layer;
        return {
          ...layer,
          ...patch,
          x: Math.max(-1, Math.min(1, patch.x ?? layer.x ?? 0)),
          y: Math.max(-1, Math.min(1, patch.y ?? layer.y ?? 0)),
          scale: Math.max(0.05, Math.min(2, patch.scale ?? layer.scale ?? DEFAULT_ARTWORK_LAYER_SCALE)),
          scaleX: Math.max(0.05, Math.min(2, patch.scaleX ?? layer.scaleX ?? layer.scale ?? DEFAULT_ARTWORK_LAYER_SCALE)),
          scaleY: Math.max(0.05, Math.min(2, patch.scaleY ?? layer.scaleY ?? layer.scale ?? DEFAULT_ARTWORK_LAYER_SCALE)),
        };
      }),
    }));
  };

  const removeArtworkLayer = (side, layerId) => {
    setArtworkLayers((currentLayers) => ({
      ...currentLayers,
      [side]: (currentLayers[side] || []).filter((layer) => layer.id !== layerId),
    }));
    if (selectedArtworkLayerId === layerId) setSelectedArtworkLayerId('');
  };

  const confirmRemoveArtworkLayer = (side, layerId) => {
    const layer = (artworkLayers[side] || []).find((entry) => entry.id === layerId);
    if (!layer) return;
    const confirmed = window.confirm(`Remove ${layer.name || 'this artwork layer'}?`);
    if (!confirmed) return;
    removeArtworkLayer(side, layerId);
  };

  const reorderArtworkLayer = (side, draggedLayerId, targetLayerId) => {
    if (!draggedLayerId || draggedLayerId === targetLayerId) return;
    setArtworkLayers((currentLayers) => {
      const nextSideLayers = [...(currentLayers[side] || [])];
      const draggedIndex = nextSideLayers.findIndex((layer) => layer.id === draggedLayerId);
      const targetIndex = nextSideLayers.findIndex((layer) => layer.id === targetLayerId);
      if (draggedIndex === -1 || targetIndex === -1) return currentLayers;
      const [draggedLayer] = nextSideLayers.splice(draggedIndex, 1);
      nextSideLayers.splice(targetIndex, 0, draggedLayer);
      return { ...currentLayers, [side]: nextSideLayers };
    });
  };

  const handleArtworkSideSelect = (side) => {
    setSelectedArtworkSide(side);
    if (artworkSides.includes(side)) setView(side);
  };

  const handleArtworkTransformLimitsChange = (side, limits) => {
    setArtworkTransformLimits((currentLimits) => {
      const previous = currentLimits[side];
      const isSame = previous
        && Math.abs(previous.offsetX.min - limits.offsetX.min) < 0.001
        && Math.abs(previous.offsetX.max - limits.offsetX.max) < 0.001
        && Math.abs(previous.offsetY.min - limits.offsetY.min) < 0.001
        && Math.abs(previous.offsetY.max - limits.offsetY.max) < 0.001;
      if (isSame) return currentLimits;
      return {
        ...currentLimits,
        [side]: limits,
      };
    });
  };

  useEffect(() => {
    const handleSelectedLayerDeleteKey = (event) => {
      if (workspaceMode !== 'edit') return;
      if (!selectedArtworkLayerId) return;
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;

      event.preventDefault();
      confirmRemoveArtworkLayer(selectedArtworkSide, selectedArtworkLayerId);
    };

    window.addEventListener('keydown', handleSelectedLayerDeleteKey);
    return () => window.removeEventListener('keydown', handleSelectedLayerDeleteKey);
  }, [artworkLayers, selectedArtworkLayerId, selectedArtworkSide, workspaceMode]);

  const handleArtworkTransformChange = (side, patch) => {
    const spec = getSidePlacementSpec(productType, side);
    const limits = artworkTransformLimits[side];
    setArtworkTransforms((currentTransforms) => {
      const next = { ...currentTransforms[side], ...patch };
      next.mode = next.mode === 'expanded' ? 'expanded' : 'standard';
      if (patch.scale !== undefined) {
        const scaleMax = next.mode === 'expanded' ? EXPANDED_SCALE_MAX : spec.scaleMax;
        next.scale = Math.max(spec.scaleMin, Math.min(scaleMax, patch.scale));
      }
      if (patch.offsetX !== undefined && limits?.offsetX) {
        next.offsetX = Math.max(limits.offsetX.min, Math.min(limits.offsetX.max, patch.offsetX));
      }
      if (patch.offsetY !== undefined && limits?.offsetY) {
        next.offsetY = Math.max(limits.offsetY.min, Math.min(limits.offsetY.max, patch.offsetY));
      }
      return {
        ...currentTransforms,
        [side]: next,
      };
    });
  };

  const updateQuantity = (itemSize, nextQuantity) => {
    const numericQuantity = Math.max(0, Math.min(999, Number(nextQuantity) || 0));
    setQuantities((currentQuantities) => ({
      ...currentQuantities,
      [itemSize]: numericQuantity,
    }));
  };

  const toggleSelectedColor = (colorName) => {
    setSelectedColors((currentColors) => {
      const nextColors = currentColors.includes(colorName)
        ? currentColors.filter((item) => item !== colorName)
        : [...currentColors, colorName];
      const normalizedColors = nextColors.length ? nextColors : ['White'];
      if (!normalizedColors.includes(color)) {
        setColor(normalizedColors[0]);
      }
      setColorQuantities((currentBreakdown) => (
        Object.fromEntries(Object.entries(currentBreakdown).filter(([key]) => normalizedColors.includes(key)))
      ));
      return normalizedColors;
    });
  };

  const updateColorQuantity = (colorName, itemSize, nextQuantity) => {
    const numericQuantity = Math.max(0, Math.min(999, Number(nextQuantity) || 0));
    setColorQuantities((currentBreakdown) => ({
      ...currentBreakdown,
      [colorName]: {
        ...(currentBreakdown[colorName] || {}),
        [itemSize]: numericQuantity,
      },
    }));
  };

  const handleContactChange = (field, value) => {
    setContact((currentContact) => ({
      ...currentContact,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!contact.name.trim() || !contact.email.trim()) {
      setSubmitState({ status: 'error', message: 'Add your name and email before submitting.' });
      return;
    }

    if (!studioItems.length) {
      setSubmitState({ status: 'error', message: 'Add at least one garment to the garment list before submitting.' });
      return;
    }

    setSubmitState({
      status: 'loading',
      message: 'Preparing your garment brief…',
      progress: 12,
      detail: 'Flattening artwork layers and building the upload package.',
    });

    const itemsForSubmission = studioItems;
    const submittedSides = new Set(
      itemsForSubmission.flatMap((item) => (item.artwork || []).map((entry) => entry.side))
    );
    const submittedPrintLocation = submittedSides.has('front') && submittedSides.has('back')
      ? 'both'
      : submittedSides.has('back') ? 'back' : 'front';
    const selectedSizes = itemsForSubmission.flatMap((item) => [
      ...item.quantities.map(({ option }) => sizeCode(option)),
      ...(item.colorBreakdown || []).flatMap((entry) => entry.sizes.map(({ option }) => sizeCode(option))),
    ]);
    const formData = new FormData();
    const submittedExtras = {
      needsStorefront: false,
      needsProductPhotography: false,
      needsCampaignSupport: false,
    };
    formData.append('name', contact.name.trim());
    formData.append('email', contact.email.trim());
    formData.append('phone', contact.phone.trim());
    formData.append('expectedDropDate', contact.expectedDropDate);
    const submittedColors = [...new Set(itemsForSubmission.flatMap((item) => item.selectedColors || []))];
    formData.append('colorPreference', submittedColors.length === 1 ? getColorPreference(submittedColors[0]) : 'custom');
    formData.append('sizeRanges', (selectedSizes.length ? selectedSizes : ['m']).join(','));
    formData.append('print_location', submittedPrintLocation);
    formData.append('specialInstructions', brief);
    formData.append('studioPayload', JSON.stringify({
      sessionId: studioSessionId || createStudioSessionId(),
      items: serializeStudioItemsForSubmission(itemsForSubmission),
      extras: submittedExtras,
      contact,
      workspaceMode,
      view,
    }));
    formData.append('artworkType', itemsForSubmission.some((item) => item.artwork.length) ? 'upload' : 'request');
    formData.append('needsStorefront', 'false');
    formData.append('needsProductPhotography', 'false');
    formData.append('needsCampaignSupport', 'false');

    await Promise.all(itemsForSubmission.map(async (item) => {
      await Promise.all(item.artwork.map(async (entry) => {
        const uploadFile = entry.file || (entry.dataUrl ? await dataUrlToFile(entry.dataUrl, entry.name, entry.type) : null);
        if (uploadFile) formData.append('files', uploadFile, `${item.productLabel}-${entry.label}-${entry.name}`);

        await Promise.all((entry.layers || []).map(async (layer, layerIndex) => {
          const layerDataUrl = layer.dataUrl || layer.url;
          if (!layerDataUrl) return;
          const layerFile = await dataUrlToWebPFile(layerDataUrl, layer.name || `layer-${layerIndex + 1}`);
          const layerFilename = [
            safeUploadName(item.productLabel || item.productType || 'garment'),
            safeUploadName(entry.side || entry.label || 'side'),
            `layer-${String(layerIndex + 1).padStart(2, '0')}`,
            layerFile.name,
          ].join('-');
          formData.append('files', layerFile, layerFilename);
        }));
      }));
    }));

    formData.append('studioSessionId', studioSessionId || createStudioSessionId());

    try {
      setSubmitState({
        status: 'loading',
        message: 'Uploading your garment brief…',
        progress: 28,
        detail: 'Sending artwork, garment list, quantities and contact details.',
      });
      const response = await api.post('/api/custom-requests/', formData, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          const uploadPercent = Math.round((event.loaded / event.total) * 52);
          setSubmitState({
            status: 'loading',
            message: 'Uploading your garment brief…',
            progress: Math.min(80, 28 + uploadPercent),
            detail: `${Math.round((event.loaded / event.total) * 100)}% uploaded. The PDF and emails are prepared after upload.`,
          });
        },
      });
      setSubmitState({
        status: 'loading',
        message: 'Brief submitted. Preparing PDF and emails…',
        progress: 92,
        detail: 'The server accepted the brief and the PDF/email job is being queued.',
      });
      setSubmitResetCountdown(5);
      setSubmitState({
        status: 'success',
        message: `Thank you. Garment brief received. Reference ${response.data.reference}.`,
        progress: 100,
        detail: response.data.delivery === 'queued'
          ? 'Your PDF brief and emails are now being generated in the background.'
          : 'Your PDF brief and emails have been sent.',
      });
    } catch (error) {
      setSubmitState({
        status: 'error',
        message: error.response?.data?.message || 'Could not send the request. Please try again.',
        progress: 100,
        detail: error.response?.status
          ? `Server responded with HTTP ${error.response.status}.`
          : 'The request did not finish. Please check your connection and try again.',
      });
    }
  };

  const renderGarmentBriefPanel = () => (
    <div className="merch-playground-brief-stage" data-testid="studio-garment-brief">
      <div className="merch-playground-brief-copy">
        <span>Submit this</span>
        <h3>Garment Brief</h3>
      </div>

      {submitState.status === 'success' ? (
        <div className="merch-playground-brief-success" role="status" aria-live="polite">
          <div className="merch-playground-brief-success-icon">
            <FaCheck />
          </div>
          <div className="merch-playground-brief-success-copy">
            <span>Brief received</span>
            <h4>{submitState.message}</h4>
            {submitState.detail && <p>{submitState.detail}</p>}
            <strong>
              Resetting studio in {submitResetCountdown ?? 5}
            </strong>
          </div>
        </div>
      ) : (
      <>
      <div className="merch-playground-brief-layout">
        <div className="merch-playground-brief-card">
          <div className="merch-playground-brief-card-header">
            <span>Contact details</span>
            <strong>{studioItems.length} garment{studioItems.length === 1 ? '' : 's'}</strong>
          </div>

          <div className="merch-playground-contact-grid">
            <label>
              <span>Name</span>
              <input value={contact.name} onChange={(event) => handleContactChange('name', event.target.value)} />
            </label>
            <label>
              <span>Email</span>
              <input type="email" value={contact.email} onChange={(event) => handleContactChange('email', event.target.value)} />
            </label>
            <label>
              <span>Phone</span>
              <input value={contact.phone} onChange={(event) => handleContactChange('phone', event.target.value)} />
            </label>
            <DatePicker
              id="playground-expected-drop-date"
              label="Target delivery date"
              value={contact.expectedDropDate}
              onChange={(nextValue) => handleContactChange('expectedDropDate', nextValue)}
            />
            <label className="merch-playground-notes">
              <span>Brief notes</span>
              <textarea
                value={contact.notes}
                onChange={(event) => handleContactChange('notes', event.target.value)}
                placeholder="Anything specific about your T-shirt or hoodie deadline, artwork, sizes or print placement?"
              />
            </label>
          </div>
        </div>
      </div>

      {submitState.message && (
        <div className={`merch-playground-message is-${submitState.status}`}>
          <div className="merch-playground-submit-progress-copy">
            <strong>{submitState.message}</strong>
            {submitState.detail && <span>{submitState.detail}</span>}
          </div>
          {submitState.status === 'loading' && (
            <div
              className="merch-playground-submit-progress"
              aria-label="Garment brief submission progress"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={submitState.progress || 0}
              role="progressbar"
            >
              <span style={{ width: `${Math.max(5, submitState.progress || 0)}%` }} />
            </div>
          )}
        </div>
      )}

      <div className="merch-playground-brief-actions">
        <button
          type="button"
          className="merch-playground-submit"
          onClick={handleSubmit}
          disabled={!studioItems.length || submitState.status === 'loading'}
        >
          <FaShoppingCart />
          {submitState.status === 'loading' ? 'Sending brief' : 'Submit garment brief'}
        </button>
      </div>
      </>
      )}
    </div>
  );

  const playground = (
    <div className={`merch-playground ${isStudioFullscreen ? 'is-fullscreen' : ''}`}>
      <div className="merch-playground-header">
        {isStudioFullscreen && (
          globalOnly ? (
            <span className="merch-playground-header-logo" aria-label="That Shirt Show">
              <img src="/img/logo-inverted.png" alt="That Shirt Show" />
            </span>
          ) : (
          <Link to="/" className="merch-playground-header-logo" aria-label="That Shirt Show home">
            <img src="/img/logo-inverted.png" alt="That Shirt Show" />
          </Link>
          )
        )}
        <div className="merch-playground-header-copy">
          <h2 className="merch-heading text-4xl leading-none text-white sm:text-5xl">
            Design your custom tee or hoodie
          </h2>
        </div>
        <div className="merch-playground-header-actions">
          {!isGarmentListStep && (
            <div className="studio-workspace-toggle" role="tablist" aria-label="Studio mode">
              <button
                type="button"
                role="tab"
                className={workspaceMode === 'edit' ? 'is-active' : ''}
                aria-selected={workspaceMode === 'edit'}
                onClick={() => setWorkspaceMode('edit')}
                data-testid="studio-mode-edit"
              >
                Edit
              </button>
              <button
                type="button"
                role="tab"
                className={workspaceMode === 'preview' ? 'is-active' : ''}
                aria-selected={workspaceMode === 'preview'}
                onClick={() => setWorkspaceMode('preview')}
                data-testid="studio-mode-preview"
              >
                Preview
              </button>
            </div>
          )}
          <button
            type="button"
            className="merch-playground-icon-button"
            onClick={() => {
              if (globalOnly) {
                setIsGlobalOpen(false);
                window.dispatchEvent(new CustomEvent(CLOSE_MERCH_PLAYGROUND_EVENT));
                return;
              }
              setIsFullScreen((current) => !current);
            }}
            aria-label={isStudioFullscreen ? 'Collapse full screen studio' : 'Expand full screen studio'}
          >
            {isStudioFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>

      <div className="merch-playground-grid">
        <div className={`merch-playground-stage ${isGarmentListStep ? 'is-brief-stage' : ''}`} aria-label={isGarmentListStep ? 'Garment brief submission' : 'Tee and hoodie preview studio'}>
          {isGarmentListStep ? renderGarmentBriefPanel() : (
          <>
          <div className="merch-playground-studio-line" />
          {workspaceMode === 'preview' ? (
            <StudioPreviewPanel
              productType={productType}
              productColor={productColor}
              previewColorName={color}
              artwork={artwork}
              artworkLayers={artworkLayers}
              artworkTransforms={artworkTransforms}
              selectedArtworkSide={selectedArtworkSide}
              onArtworkSideSelect={handleArtworkSideSelect}
              previewColors={checkedPreviewColors}
              previewColorTotals={previewColorTotals}
              onPreviewColorSelect={setColor}
            />
          ) : (
            <>
              <div className="studio-edit-side-thumbs studio-preview-thumbs" aria-label="Garment side">
                {artworkSides.map((side) => (
                  <button
                    key={side}
                    type="button"
                    className={`studio-preview-thumb ${selectedArtworkSide === side ? 'is-active' : ''}`}
                    onClick={() => handleArtworkSideSelect(side)}
                  >
                    <StudioMockupComposer
                      productType={productType}
                      productColor={productColor}
                      colorName={color}
                      view={side}
                      artwork={artwork}
                      artworkLayers={artworkLayers}
                      artworkTransforms={artworkTransforms}
                      mockupVariant="flat"
                      mockupOption={{
                        id: `${side}-flat`,
                        label: side,
                        view: side,
                        variant: 'flat',
                        plateView: side,
                        calibrationId: `${side}-2`,
                      }}
                      usePhotoPlates
                      compact
                    />
                    <span>{side}</span>
                  </button>
                ))}
              </div>
              <StudioCanvas2D
                productType={productType}
                productColor={productColor}
                view={view}
                artwork={artwork}
                artworkLayers={artworkLayers}
                selectedArtworkLayerId={selectedArtworkLayerId}
                selectedArtworkSide={selectedArtworkSide}
                artworkTransforms={artworkTransforms}
                onArtworkSideSelect={handleArtworkSideSelect}
                onArtworkLayerSelect={handleArtworkLayerSelect}
                onArtworkLayerTransformChange={handleArtworkLayerTransformChange}
                onArtworkTransformChange={handleArtworkTransformChange}
                onArtworkTransformLimitsChange={handleArtworkTransformLimitsChange}
                canvasZoom={canvasZoom}
                resetViewSignal={canvasResetSignal}
              />
              <div className="studio-edit-colour-thumbs studio-preview-colours" aria-label="Preview colours">
                {checkedPreviewColors.map((colorName) => (
                  <button
                    key={colorName}
                    type="button"
                    className={`studio-preview-colour-thumb ${color === colorName ? 'is-active' : ''}`}
                    onClick={() => setColor(colorName)}
                  >
                    <StudioMockupComposer
                      productType={productType}
                      productColor={COLOR_HEX[colorName] || productColor}
                      colorName={colorName}
                      view={selectedArtworkSide}
                      artwork={artwork}
                      artworkLayers={artworkLayers}
                      artworkTransforms={artworkTransforms}
                      mockupVariant="flat"
                      mockupOption={{
                        id: `${selectedArtworkSide}-flat`,
                        label: selectedArtworkSide,
                        view: selectedArtworkSide,
                        variant: 'flat',
                        plateView: selectedArtworkSide,
                        calibrationId: `${selectedArtworkSide}-2`,
                      }}
                      usePhotoPlates
                      compact
                    />
                    <span className="studio-preview-colour-count" aria-label={`${previewColorTotals[colorName] || 0} ${colorName} garments`}>
                      {previewColorTotals[colorName] || 0}
                    </span>
                    <span>{colorName}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {workspaceMode === 'edit' && (
            <label className="merch-playground-stage-zoom" htmlFor="playground-stage-canvas-zoom">
              <input
                id="playground-stage-canvas-zoom"
                type="range"
                min="0.55"
                max="1.8"
                step="0.01"
                value={canvasZoom}
                onChange={(event) => setCanvasZoom(Number(event.target.value))}
              />
            </label>
          )}
          {workspaceMode === 'edit' && (
            <button
              type="button"
              className="merch-playground-stage-reset"
              onClick={() => {
                setCanvasZoom(1);
                setCanvasResetSignal((signal) => signal + 1);
              }}
            >
              Reset
            </button>
          )}
          <div className="merch-playground-stage-status">
            <label>
              <span>Product</span>
              <select
                value={productType}
                data-testid="studio-product-select"
                onChange={(event) => setProductType(event.target.value)}
                aria-label="Preview product"
              >
                {Object.entries(PRODUCT_OPTIONS).map(([value, option]) => (
                  <option key={value} value={value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
          </>
          )}
        </div>

        <aside className={`merch-playground-control-panel is-${isGarmentListStep ? 'list' : formStep}-step`} aria-label="Design controls">
          {formStep === 'build' ? (
            <>
              <div className="merch-playground-panel-tabs" aria-label="Design Studio sections">
                <button type="button" className="is-active">Build item</button>
                <button type="button" disabled={!studioItems.length} onClick={() => setFormStep('list')}>
                  Garment list{studioItems.length ? ` (${studioItems.length})` : ''}
                </button>
              </div>

              <div className="merch-playground-build-scroll">
              <div className="merch-playground-build-tabs" aria-label="Build item details">
                {BUILD_ITEM_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={buildItemTab === tab.key ? 'is-active' : ''}
                    onClick={() => setBuildItemTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {buildItemTab === 'colors' && (
                <div className="merch-playground-tab-panel">
                  {product.singleQuantity ? (
                    <div className="merch-playground-sticker-quantity-row">
                      <div className="merch-playground-row">
                        <label htmlFor="playground-shape">{product.attributeLabel}</label>
                        <span className="merch-playground-select-wrap">
                          <select
                            id="playground-shape"
                            value={selectedOption}
                            onChange={(event) => {
                              setSelectedOption(event.target.value);
                              setQuantities({});
                              setColorQuantities({});
                            }}
                          >
                            {product.options.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </span>
                      </div>
                      <div className="merch-playground-quantity-row">
                        <span>Quantity</span>
                        <div className="merch-playground-stepper">
                          <button
                            type="button"
                            onClick={() => updateQuantity(selectedOption, (quantities[selectedOption] || 0) - 1)}
                            aria-label={`Remove one ${product.label} ${selectedOption}`}
                          >
                            <FaMinus />
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="999"
                            value={quantities[selectedOption] || 0}
                            onChange={(event) => updateQuantity(selectedOption, event.target.value)}
                            aria-label={`${product.label} ${selectedOption} quantity`}
                          />
                          <button
                            type="button"
                            onClick={() => updateQuantity(selectedOption, (quantities[selectedOption] || 0) + 1)}
                            aria-label={`Add one ${product.label} ${selectedOption}`}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="merch-playground-color-grid">
                        {product.colors.map((colorName) => (
                          <label
                            key={colorName}
                            className={`merch-playground-color-check ${selectedColors.includes(colorName) ? 'is-active' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedColors.includes(colorName)}
                              onChange={() => toggleSelectedColor(colorName)}
                            />
                            <span style={{ '--swatch-color': COLOR_HEX[colorName] || '#f3f2ef' }} />
                            <strong>{colorName}</strong>
                          </label>
                        ))}
                      </div>
                      <div className="merch-playground-color-breakdown">
                        {selectedColors.map((colorName) => (
                          <div key={colorName} className="merch-playground-color-breakdown-row">
                            <strong
                              style={{ '--swatch-color': COLOR_HEX[colorName] || '#f3f2ef' }}
                            >
                              {colorName}
                            </strong>
                            <div className="merch-playground-color-size-grid">
                              {product.options.map((itemSize) => (
                                <label key={`${colorName}-${itemSize}`}>
                                  <span>{itemSize}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="999"
                                    value={colorQuantities[colorName]?.[itemSize] || 0}
                                    onChange={(event) => updateColorQuantity(colorName, itemSize, event.target.value)}
                                  />
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {buildItemTab === 'instructions' && (
                <div className="merch-playground-tab-panel is-instructions">
                  <label className="merch-playground-notes merch-playground-notes--unlabelled">
                    <textarea
                      value={itemInstructions}
                      onChange={(event) => setItemInstructions(event.target.value)}
                      placeholder="Specify anything we need to know about colours, quantities, print placement or your deadline."
                    />
                  </label>
                </div>
              )}

              {buildItemTab === 'layers' && (
                <div className="merch-playground-tab-panel">
                  <div className="merch-playground-side-tabs" role="group" aria-label="Garment side">
                    {artworkSides.map((side) => (
                      <button
                        key={side}
                        type="button"
                        className={selectedArtworkSide === side ? 'is-active' : ''}
                        onClick={() => handleArtworkSideSelect(side)}
                      >
                        {side === 'front' ? 'Front' : 'Back'}
                        <small>{(artworkLayers[side] || []).length} layer{(artworkLayers[side] || []).length === 1 ? '' : 's'}</small>
                      </button>
                    ))}
                  </div>

                  <div className="merch-playground-stencil-panel" data-testid="studio-artwork-layers">
                    <div className="merch-playground-layer-list">
                      {selectedArtworkLayers.length ? selectedArtworkLayers.map((layer) => (
                        <div
                          key={layer.id}
                          className={`merch-playground-layer-row ${selectedArtworkLayer?.id === layer.id ? 'is-active' : ''}`}
                          draggable
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', layer.id);
                          }}
                          onDragOver={(event) => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            reorderArtworkLayer(selectedArtworkSide, event.dataTransfer.getData('text/plain'), layer.id);
                          }}
                        >
                          <button
                            type="button"
                            className="merch-playground-layer-select"
                            onClick={() => handleArtworkLayerSelect(selectedArtworkSide, layer.id)}
                          >
                            <img src={layer.url} alt="" />
                            <span>{layer.name}</span>
                          </button>
                          <button
                            type="button"
                            className="merch-playground-layer-remove"
                            onClick={() => confirmRemoveArtworkLayer(selectedArtworkSide, layer.id)}
                            aria-label={`Remove ${layer.name}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )) : null}
                    </div>

                    <label className="merch-playground-add-layer">
                      <FaUpload />
                      <span>Add artwork</span>
                      <input
                        ref={(node) => {
                          artworkInputRefs.current[selectedArtworkSide] = node;
                        }}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(event) => {
                          handleArtworkUpload(selectedArtworkSide, event.target.files);
                          event.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}

              </div>
              <div className="merch-playground-panel-actions">
                <button
                  type="button"
                  className="merch-playground-submit"
                  onClick={addCurrentItemToStudio}
                  disabled={!canAddCurrentItem}
                >
                  {editingItemId ? 'Update garment' : 'Add garment'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="merch-playground-panel-tabs" aria-label="Design Studio sections">
                <button type="button" onClick={() => setFormStep('build')}>Build item</button>
                <button type="button" className="is-active">
                  Garment list{studioItems.length ? ` (${studioItems.length})` : ''}
                </button>
              </div>

              <div className="merch-playground-wishlist is-list-step">
                <div className="merch-playground-wishlist-header">
                  <span>Garment list</span>
                  <strong>{studioItems.length} garment{studioItems.length === 1 ? '' : 's'}</strong>
                </div>
                {studioItems.length > 0 ? (
                  <div className="merch-playground-wishlist-list">
                    {studioItems.map((item) => (
                      <div key={item.id} className="merch-playground-wishlist-item">
                        <div>
                          <strong>{item.productLabel}</strong>
                          {item.colorBreakdown?.length ? (
                            <small>{item.colorBreakdown.map((entry) => `${entry.color}: ${entry.sizes.map(({ quantity, option }) => `${quantity} ${option}`).join(', ')}`).join(' | ')}</small>
                          ) : (
                            <small>{item.attributeValue || 'No option'} / {item.quantities.map(({ quantity, option }) => `${quantity} ${option}`).join(', ')}</small>
                          )}
                          {item.itemInstructions ? (
                            <small>{item.itemInstructions}</small>
                          ) : null}
                          <small>{item.artwork.map((entry) => entry.name).join(', ') || 'No artwork uploaded'}</small>
                        </div>
                        <div className="merch-playground-wishlist-actions">
                          <button type="button" onClick={() => editStudioItem(item)}>Edit</button>
                          <button type="button" onClick={() => removeStudioItem(item.id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="merch-playground-wishlist-empty">Add a garment before opening the garment list.</p>
                )}
              </div>

              <div className="merch-playground-panel-actions">
                <button
                  type="button"
                  className="merch-playground-secondary"
                  onClick={() => {
                    resetCurrentBuild();
                    setFormStep('build');
                  }}
                >
                  Add garment
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );

  return (
    <section
      id={globalOnly ? undefined : 'merch-playground'}
      data-testid={globalOnly ? 'studio-global' : undefined}
      className={`merch-playground-shell ${globalOnly ? 'is-global-studio' : 'py-8 sm:py-10'} ${isStudioFullscreen ? 'is-fullscreen-open' : ''} ${!isStudioVisible ? 'is-closed' : ''}`}
      aria-hidden={!isStudioVisible ? 'true' : undefined}
    >
      {playground}
    </section>
  );
};

MerchPlayground.propTypes = {
  globalOnly: PropTypes.bool,
};

export default MerchPlayground;
